/**
 * sticky-header — moved out of sections/header.liquid {% javascript %}.
 *
 * Shopify compiled that block to compiled_assets/scripts.js, which left the
 * mobile browser document-load indicator spinning. This asset loads with defer,
 * initializes after window "load", and does not cloneNode the header section
 * (cloning during load re-queued resources on mobile).
 */
(function () {
  if (customElements.get('sticky-header')) return;

  class StickyHeader extends HTMLElement {
    connectedCallback() {
      if (this._acBound) return;
      this._acBound = true;

      const start = () => {
        try {
          this.init();
        } catch (e) {
          console.error(e);
        }
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start, { once: true });
      } else {
        start();
      }
    }

    init() {
      this.header = document.querySelector('.section-header');
      if (!this.header) return;

      this.headerIsAlwaysSticky =
        this.getAttribute('data-sticky-type') === 'always' ||
        this.getAttribute('data-sticky-type') === 'reduce-logo-size';
      this.headerBounds = {};

      this.cacheHeaderHeights();

      window.matchMedia('(max-width: 990px)').addEventListener('change', () => this.cacheHeaderHeights());
      window.addEventListener('resize', () => {
        window.clearTimeout(this.headerResizeTimer);
        this.headerResizeTimer = setTimeout(() => this.cacheHeaderHeights(), 150);
      });

      if (this.headerIsAlwaysSticky) {
        this.header.classList.add('shopify-section-header-sticky');
      }

      this.currentScrollTop = 0;
      this.preventReveal = false;
      this.predictiveSearch = this.querySelector('predictive-search');

      this.onScrollHandler = this.onScroll.bind(this);
      this.onPageShow = () => this.syncRestoredScroll();
      this.hideHeaderOnScrollUp = () => {
        this.preventReveal = true;
      };

      this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.addEventListener('scroll', this.onScrollHandler, { passive: true });
      window.addEventListener('pageshow', this.onPageShow);

      this.createObserver();
      this.syncRestoredScroll();
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          this.setHeaderHeight();
          this.repairStickyGap();
        });
      }
      window.addEventListener('load', () => this.repairStickyGap(), { once: true });
    }

    /**
     * A reload while scrolled sticks the filter before --header-height matches
     * the menu. The bar then sits one announcement-bar too low until it
     * unsticks at the top of the page. Sync the height, then re-stick.
     */
    syncRestoredScroll() {
      if (!this.header) return;
      this.onScroll();
      this.setHeaderHeight();
      this.repairStickyGap();
    }

    repairStickyGap() {
      const bar = document.getElementById('main-collection-filters');
      if (!bar || !this.header || this._repairingGap) return;
      const y = window.scrollY || document.documentElement.scrollTop;
      if (y < 1) return;
      const headerRect = this.header.getBoundingClientRect();
      if (headerRect.bottom < 8) return;
      const gap = bar.getBoundingClientRect().top - headerRect.bottom;
      if (gap < 8) return;

      /* Move only the filter. Hiding the menu or jumping scroll reads as a page flash. */
      this._repairingGap = true;
      bar.style.top = `${Math.round(headerRect.bottom) - 1}px`;
      this._repairingGap = false;
    }

    /** Live measure only — never cloneNode the section during/after load. */
    cacheHeaderHeights() {
      if (!this.header) return;
      this.setHeaderHeight();
    }

    setHeaderHeight() {
      if (!this.header) return;
      /* Search inserts a spacer into the fixed bars. Measuring then sticks a
         too-tall menu after the modal closes. */
      if (document.documentElement.classList.contains('search-open')) return;
      const announce = document.querySelector('.announcement-bar-section');
      if (announce) {
        const bar = announce.querySelector('.utility-bar') || announce;
        document.documentElement.style.setProperty('--announcement-height', `${bar.offsetHeight}px`);
      }
      this.header.classList.add('ac-header-measure');
      const inner = this.header.querySelector('.header') || this.header;
      const h = inner.offsetHeight;
      this.header.classList.remove('ac-header-measure');
      document.documentElement.style.setProperty('--header-height', `${h}px`);
      const key = this.header.classList.contains('header-compact')
        ? '--header-compact-height'
        : '--header-expanded-height';
      document.documentElement.style.setProperty(key, `${h}px`);
    }

    publishHeaderHeight(isCompact) {
      const key = isCompact ? '--header-compact-height' : '--header-expanded-height';
      const known = document.documentElement.style.getPropertyValue(key).trim();
      if (known) {
        document.documentElement.style.setProperty('--header-height', known);
        return;
      }
      this.setHeaderHeight();
    }

    disconnectedCallback() {
      this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      if (this.onScrollHandler) {
        window.removeEventListener('scroll', this.onScrollHandler);
      }
      if (this.onPageShow) {
        window.removeEventListener('pageshow', this.onPageShow);
      }
    }

    createObserver() {
      if (!this.header || typeof IntersectionObserver === 'undefined') return;
      const observer = new IntersectionObserver((entries, obs) => {
        this.headerBounds = entries[0].intersectionRect;
        obs.disconnect();
      });
      observer.observe(this.header);
    }

    onScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

      if (this.headerIsAlwaysSticky) {
        this.header.classList.toggle('scrolled-past-header', scrollTop > 0);
        /* Mobile: never compact — stable icon layout / no shrink on scroll */
        if (window.matchMedia('(max-width: 989px)').matches) {
          if (this.header.classList.contains('header-compact')) {
            this.header.classList.remove('header-compact');
            document.documentElement.classList.remove('header-compact');
            const expanded = getComputedStyle(document.documentElement)
              .getPropertyValue('--header-expanded-height')
              .trim();
            if (expanded) {
              document.documentElement.style.setProperty('--header-height', expanded);
            } else {
              this.setHeaderHeight();
            }
          }
          this.currentScrollTop = scrollTop;
          return;
        }
        const wasCompact = this.header.classList.contains('header-compact');
        const COMPACT_ENTER = 100;
        const COMPACT_EXIT = 40;
        const isCompact = wasCompact ? scrollTop > COMPACT_EXIT : scrollTop > COMPACT_ENTER;
        this.header.classList.toggle('header-compact', isCompact);
        document.documentElement.classList.toggle('header-compact', isCompact);
        if (wasCompact !== isCompact) this.publishHeaderHeight(isCompact);
        this.currentScrollTop = scrollTop;
        return;
      }

      if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        if (this.preventHide) return;
        requestAnimationFrame(this.hide.bind(this));
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        if (!this.preventReveal) {
          requestAnimationFrame(this.reveal.bind(this));
        } else {
          window.clearTimeout(this.isScrolling);
          this.isScrolling = setTimeout(() => {
            this.preventReveal = false;
          }, 66);
          requestAnimationFrame(this.hide.bind(this));
        }
      } else if (scrollTop <= this.headerBounds.top) {
        this.header.classList.remove('scrolled-past-header');
        requestAnimationFrame(this.reset.bind(this));
      }

      this.currentScrollTop = scrollTop;
    }

    hide() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      this.closeMenuDisclosure();
      this.closeSearchModal();
    }

    reveal() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-sticky', 'animate');
      this.header.classList.remove('shopify-section-header-hidden');
    }

    reset() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
    }

    closeMenuDisclosure() {
      this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
      this.disclosures.forEach((disclosure) => {
        if (disclosure && typeof disclosure.close === 'function') disclosure.close();
      });
    }

    closeSearchModal() {
      this.searchModal = this.searchModal || this.header.querySelector('details-modal');
      if (this.searchModal && typeof this.searchModal.close === 'function') {
        this.searchModal.close(false);
      }
    }
  }

  customElements.define('sticky-header', StickyHeader);
})();
